/**
 * Built-in Business + Portfolio tabs, plus user-created tabs (rename / delete / add).
 */

import React, { useState } from 'react';
import type { CustomTab, TabId } from './types';

const MAX_CUSTOM_TABS = 12;

interface PerformanceTabStripProps {
  tab: TabId;
  customTabs: CustomTab[];
  onSelect: (tab: TabId) => void;
  onAdd: () => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  canAdd: boolean;
  t: (key: string) => string;
}

const BUILTIN: { id: TabId; labelKey: string; fallback: string }[] = [
  { id: 'business', labelKey: 'kpi.perfTabBusiness', fallback: 'Business' },
  { id: 'portfolio', labelKey: 'kpi.perfTabPortfolio', fallback: 'Portfolio' }
];

export const PerformanceTabStrip: React.FC<PerformanceTabStripProps> = ({
  tab,
  customTabs,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  canAdd,
  t
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const atLimit = customTabs.length >= MAX_CUSTOM_TABS;

  const startRename = (item: CustomTab) => {
    setEditingId(item.id);
    setDraft(item.name);
  };

  const commitRename = () => {
    if (!editingId) return;
    const name = draft.trim().slice(0, 40);
    if (name) onRename(editingId, name);
    setEditingId(null);
  };

  const tabClass = (active: boolean) =>
    `px-3 py-1.5 text-sm rounded ${
      active
        ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
        : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] hover:opacity-90'
    }`;

  return (
    <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label={t('kpi.perfTabs') || 'Performance'}>
      {BUILTIN.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={tab === item.id}
          className={tabClass(tab === item.id)}
          onClick={() => onSelect(item.id)}
        >
          {t(item.labelKey) || item.fallback}
        </button>
      ))}

      {customTabs.map((item) => (
        <div key={item.id} className="relative inline-flex items-center">
          {editingId === item.id ? (
            <input
              autoFocus
              value={draft}
              maxLength={40}
              aria-label={t('kpi.perfRenameTab') || 'Rename tab'}
              className="px-2 py-1 pr-4 text-sm rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-surface-text)] w-28"
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commitRename}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitRename();
                if (event.key === 'Escape') setEditingId(null);
              }}
            />
          ) : (
            <button
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={`${tabClass(tab === item.id)} pr-4`}
              title={t('kpi.perfRenameTab') || 'Double-click to rename'}
              onClick={() => onSelect(item.id)}
              onDoubleClick={() => startRename(item)}
            >
              {item.name}
            </button>
          )}
          <button
            type="button"
            className="absolute top-0 right-0.5 p-0 leading-none text-[11px] bg-transparent text-[var(--color-secondary-text)] hover:opacity-70"
            aria-label={`${t('kpi.perfDeleteTab') || 'Delete tab'} ${item.name}`}
            onClick={() => onDelete(item.id)}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        disabled={atLimit || !canAdd}
        className="px-2 py-1.5 text-sm rounded border border-dashed border-[var(--color-border)] text-[var(--color-secondary-text)] hover:opacity-90 disabled:opacity-40"
        title={atLimit ? t('kpi.perfTabLimit') || 'You can add up to 12 tabs.' : t('kpi.perfAddTab') || 'Add tab'}
        onClick={onAdd}
      >
        +
      </button>
    </div>
  );
};
